import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spin, message } from 'antd'
import { useHistory, useLocation } from 'react-router-dom'
import { getAicDashboardDatasets, getAicLedgerDatasets } from '../../request'
import TerminalDetail from './TerminalDetail'
import {
  AUDIT_PENDING_TITLE,
  DEFAULT_SORT,
  EMPTY,
  LEDGER_STATUSES,
  countLedgerSources,
  deriveLedgerStatusKey,
  filterRows,
  formatBytes,
  formatDateOnly,
  formatDateTimeMinute,
  formatMdDigest,
  formatMdSummary,
  formatPlan,
  formatRowsCount,
  ledgerCsv,
  ledgerCsvFilename,
  ledgerStatusMeta,
  mapLegacyDatasetsToLedger,
  nextSort,
  projectCode,
  sortIndicator,
  sortRows,
  sourceOf,
  termDisplay,
  unwrapLedgerPayload,
  unwrapRows
} from './terminalModel'

/**
 * 逐条台账 17 列（列表页改版契约 §1.1 冻结）。
 * audit: 审计列（LC-2505 数据未接入）一律渲染 —，不排行、不导出值。
 */
const COLUMNS = [
  { key: 'source', label: '来源', width: 56, sortable: true },
  { key: 'projectCode', label: '编号', width: 76, sortable: true },
  { key: 'unitName', label: '数据单元', width: 180, sortable: true },
  { key: 'rowCount', label: '数据量', width: 76, sortable: true, numeric: true },
  { key: 'attachmentBytes', label: '占用空间', width: 82, sortable: true, numeric: true, title: '仅附件体积，不含业务表物理大小' },
  { key: 'owner', label: '负责人', width: 80, sortable: true },
  { key: 'ledgerStatus', label: '状态', width: 86, sortable: true },
  { key: 'anomalyReason', label: '异常原因 / 状态说明', width: 156, sortable: true },
  { key: 'auditTodo', label: '整改需求', width: 94, audit: true },
  { key: 'dictionaryLength', label: 'MD 文档', width: 174, sortable: true },
  { key: 'auditVerdict', label: '验收结论', width: 70, audit: true },
  { key: 'auditAi', label: 'AI', width: 54, audit: true },
  { key: 'department', label: '部门', width: 80, sortable: true },
  { key: 'auditHealth', label: '体检三维', width: 70, audit: true },
  { key: 'planDeadline', label: '更新计划', width: 110, sortable: true },
  { key: 'lastSuccessfulUploadAt', label: '最近上传', width: 116, sortable: true },
  { key: 'dataEnd', label: '数据止于', width: 90, sortable: true }
]

const SOURCE_OPTIONS = [
  { value: '', label: '全部来源' },
  { value: 'AIC', label: 'AIC 数据单元' },
  { value: 'Vibe', label: 'Vibe 项目' }
]

const AUDIT_ONLY_OPTIONS = {
  verdict: ['通过', '待整改', '文档型'],
  grade: ['A', 'B', 'C', 'D']
}

const EMPTY_FILTERS = { source: '', department: '', status: '', owner: '' }

const rowKeyOf = row => `${row.projectKind || 'dataset'}-${row.projectId || 'x'}-${row.unitName || ''}`

const compareZh = (left, right) => String(left).localeCompare(String(right), 'zh-CN')

const uniqueSorted = values => Array.from(new Set(values.filter(Boolean))).sort(compareZh)

const rowAnomalyClass = row => {
  if (row.anomalyStatus === 'never_uploaded') return ' term-row-never_uploaded'
  const key = deriveLedgerStatusKey(row)
  if (key === 'not_updated') return ' term-row-not_updated'
  if (key === 'not_configured') return ' term-row-not_configured'
  return ''
}

function AuditCell () {
  return <td className="term-td term-audit-pending" title={AUDIT_PENDING_TITLE}>{EMPTY}</td>
}

function MdCell ({ row, onOpenDictionary }) {
  const dictionary = row.dictionary
  if (!dictionary) return <td className="term-td term-muted" title="接口未下发数据字典信息">{EMPTY}</td>
  if (dictionary.present !== true) return <td className="term-td term-muted">无</td>
  const digest = formatMdDigest(dictionary)
  const title = dictionary.excerpt ? `${formatMdSummary(dictionary)} · ${digest}` : formatMdSummary(dictionary)
  return <td className="term-td term-mdcell">
    <button type="button" className="term-link term-md-link" title={title} onClick={event => { event.stopPropagation(); onOpenDictionary(row) }}>
      {digest}
    </button>
  </td>
}

function StatusCell ({ row }) {
  const key = deriveLedgerStatusKey(row)
  const meta = ledgerStatusMeta(key)
  if (!meta) return <td className="term-td term-muted">{EMPTY}</td>
  return <td className="term-td term-nowrap">
    <span className="term-state">
      <span className={`term-dot term-dot-${meta.key}`} />
      {meta.label}
    </span>
  </td>
}

function LedgerRow ({ row, onOpenDetail, onOpenDictionary }) {
  return <tr className={`term-row${rowAnomalyClass(row)}`} data-row-key={rowKeyOf(row)} onClick={() => onOpenDetail(row)}>
    <td className="term-td term-nowrap">
      <span className={`term-src term-src-${sourceOf(row) === 'Vibe' ? 'vibe' : 'aic'}`}>{sourceOf(row)}</span>
    </td>
    <td className="term-td term-mono term-nowrap">{projectCode(row)}</td>
    <td className="term-td term-unitcell" title={termDisplay(row.unitName)}>{termDisplay(row.unitName)}</td>
    <td className="term-td term-num term-mono">{formatRowsCount(row.rowCount)}</td>
    <td className="term-td term-num term-mono" title="仅附件体积，不含业务表物理大小">{formatBytes(row.attachmentBytes)}</td>
    <td className="term-td term-nowrap" title={termDisplay(row.owner)}>{termDisplay(row.owner)}</td>
    <StatusCell row={row} />
    <td className="term-td term-ellipsis" title={termDisplay(row.anomalyReason)}>{termDisplay(row.anomalyReason)}</td>
    <AuditCell />
    <MdCell row={row} onOpenDictionary={onOpenDictionary} />
    <AuditCell />
    <AuditCell />
    <td className="term-td term-nowrap" title={termDisplay(row.department)}>{termDisplay(row.department)}</td>
    <AuditCell />
    <td className="term-td term-nowrap" title={row.nextExpectedUploadAt ? `计划截止 ${row.nextExpectedUploadAt}` : undefined}>
      {formatPlan(row.updatePlanText, row.nextExpectedUploadAt)}
    </td>
    <td className="term-td term-nowrap">{formatDateTimeMinute(row.lastSuccessfulUploadAt)}</td>
    <td className="term-td term-nowrap">{formatDateOnly(row.dataDateRange && row.dataDateRange.end)}</td>
  </tr>
}

/** 数据源（逐条台账）：LC-2505 页面结构；审计列严格留空，未知一律 —。 */
export default function TerminalLedger () {
  const history = useHistory()
  const location = useLocation()
  const [rows, setRows] = useState([])
  const [degraded, setDegraded] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [keyword, setKeyword] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [detail, setDetail] = useState(null)
  const [reloadToken, setReloadToken] = useState(0)
  // 从 URL 带入的 projectId（「我的项目」数据集行跳转）：行加载完成后自动打开详情。
  const queryProjectId = useMemo(() => {
    const search = location.search || ''
    if (!search) return ''
    return new URLSearchParams(search).get('projectId') || ''
  }, [location.search])
  // 记录已处理的 query projectId，避免关闭详情/刷新列表后被重复打开。
  const openedQueryRef = useRef('')
  // 物理删除后的补刷定时器（后台异步删除，3s 后再刷一次），卸载时清理。
  const deletedReloadTimerRef = useRef(null)

  useEffect(() => () => {
    if (deletedReloadTimerRef.current) {
      clearTimeout(deletedReloadTimerRef.current)
      deletedReloadTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    setDegraded(false)
    getAicLedgerDatasets({})
      .then(value => {
        if (!mounted) return
        setRows(unwrapLedgerPayload(value).list)
      })
      .catch(() => getAicDashboardDatasets({})
        .then(value => {
          if (!mounted) return
          setRows(mapLegacyDatasetsToLedger(unwrapRows(value)))
          setDegraded(true)
        })
        .catch(() => {
          if (!mounted) return
          setRows([])
          setError('数据单元台账加载失败')
        }))
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [reloadToken])

  // P4：URL 带 projectId 时（从「我的项目」的数据集行进入），等行数据加载完再打开详情；
  // 列表里没有该行（已删除/不可见）时给一次提示，不自动重试。
  useEffect(() => {
    if (!queryProjectId) {
      openedQueryRef.current = ''
      return
    }
    if (loading || openedQueryRef.current === queryProjectId) return
    openedQueryRef.current = queryProjectId
    const matched = rows.find(row => String(row.projectId) === String(queryProjectId))
    if (!matched) {
      message.warning('未在数据单元总账中找到该项目，可能已删除或不在你的可见范围，请刷新后重试')
      return
    }
    setDetail({ row: matched, tab: 'overview' })
  }, [queryProjectId, loading, rows])

  // 关闭详情时用 replace 去掉 projectId，避免刷新页面又自动打开。
  const closeDetail = useCallback(() => {
    setDetail(null)
    if (!queryProjectId) return
    const params = new URLSearchParams(location.search || '')
    params.delete('projectId')
    const nextSearch = params.toString()
    history.replace({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' })
  }, [history, location.pathname, location.search, queryProjectId])

  // P5：物理删除成功后关闭详情并刷新台账；删除是后台异步任务，立即刷新可能仍看到旧行，
  // 3 秒后再补刷一次（只补一次，不轮询）。重复删除时重置定时器，避免叠加请求。
  const handleDetailDeleted = useCallback(() => {
    setDetail(null)
    setReloadToken(token => token + 1)
    if (deletedReloadTimerRef.current) clearTimeout(deletedReloadTimerRef.current)
    deletedReloadTimerRef.current = setTimeout(() => {
      deletedReloadTimerRef.current = null
      setReloadToken(token => token + 1)
    }, 3000)
  }, [])

  const handleSort = useCallback(key => setSort(current => nextSort(current, key)), [])

  const setFilter = useCallback((name, value) => {
    setFilters(current => ({ ...current, [name]: value }))
  }, [])

  const sourceCounts = useMemo(() => countLedgerSources(rows), [rows])
  const departmentOptions = useMemo(() => uniqueSorted(rows.map(row => row.department)), [rows])
  const ownerOptions = useMemo(() => uniqueSorted(rows.map(row => row.owner)), [rows])
  const statusOptions = useMemo(() => {
    const present = new Set(rows.map(row => {
      const meta = ledgerStatusMeta(deriveLedgerStatusKey(row))
      return meta ? meta.label : null
    }).filter(Boolean))
    return LEDGER_STATUSES.filter(item => present.has(item.label))
  }, [rows])

  const filtered = useMemo(
    () => sortRows(filterRows(rows, { ...filters, keyword }), sort),
    [rows, filters, keyword, sort]
  )

  const exportCsv = useCallback(() => {
    try {
      if (typeof Blob === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return
      const blob = new Blob([ledgerCsv(filtered)], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = ledgerCsvFilename()
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      setTimeout(() => URL.revokeObjectURL(url), 2000)
    } catch (error) {
      // 导出失败不应影响页面浏览；CSV 内容本身由纯函数 ledgerCsv 保证
    }
  }, [filtered])

  return <div className="term-ledger">
    {degraded && <div className="term-notice">聚合接口未上线，当前为降级视图（部分列不可用）</div>}
    {error && <div className="term-notice term-notice-error">
      {error}
      <button type="button" className="term-link" onClick={() => setReloadToken(token => token + 1)}>重试</button>
    </div>}
    <div className="term-ledger-head">
      <div className="term-ledger-head-text">
        <div className="term-page-title">逐条台账</div>
        <div className="term-page-sub">
          共 {sourceCounts.total} 条（AIC {sourceCounts.aic} + Vibe {sourceCounts.vibe}），点击任意行查看逐条明细；列可排序
        </div>
      </div>
      <button type="button" className="term-csv-btn" onClick={exportCsv}>导出当前筛选 CSV</button>
    </div>
    <div className="term-toolbar term-ledger-toolbar">
      <select className="term-select" aria-label="来源筛选" value={filters.source} onChange={event => setFilter('source', event.target.value)}>
        {SOURCE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      <select className="term-select" aria-label="部门筛选" value={filters.department} onChange={event => setFilter('department', event.target.value)}>
        <option value="">全部部门</option>
        {departmentOptions.map(value => <option key={value} value={value}>{value}</option>)}
      </select>
      <select className="term-select" aria-label="状态筛选" value={filters.status} onChange={event => setFilter('status', event.target.value)}>
        <option value="">全部状态</option>
        {statusOptions.map(item => <option key={item.key} value={item.label}>{item.label}</option>)}
      </select>
      <select className="term-select" aria-label="结论筛选" title={AUDIT_PENDING_TITLE} disabled value="">
        <option value="">全部结论</option>
        {AUDIT_ONLY_OPTIONS.verdict.map(value => <option key={value} value={value}>{value}</option>)}
      </select>
      <select className="term-select" aria-label="档位筛选" title={AUDIT_PENDING_TITLE} disabled value="">
        <option value="">全部档位</option>
        {AUDIT_ONLY_OPTIONS.grade.map(value => <option key={value} value={value}>{value}</option>)}
      </select>
      <select className="term-select" aria-label="负责人筛选" value={filters.owner} onChange={event => setFilter('owner', event.target.value)}>
        <option value="">全部负责人</option>
        {ownerOptions.map(value => <option key={value} value={value}>{value}</option>)}
      </select>
      <input
        className="term-search"
        type="search"
        value={keyword}
        placeholder="搜索编号 / 名称 / 负责人 / 部门 / 状态说明"
        aria-label="搜索编号 / 名称 / 负责人 / 部门 / 状态说明"
        onChange={event => setKeyword(event.target.value)}
      />
      <span className="term-count">{filtered.length} / {rows.length} 条</span>
    </div>
    <Spin spinning={loading}>
      <table className="term-table term-ledger-table">
        <colgroup>
          {COLUMNS.map(column => <col key={column.key} style={{ width: `${column.width}px` }} />)}
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map(column => column.audit
              ? <th key={column.key} className="term-th term-th-static term-th-audit" scope="col" title={AUDIT_PENDING_TITLE}>
                {column.label}
              </th>
              : <th
                key={column.key}
                className={`term-th ${column.numeric ? 'term-num' : ''} ${sort.key === column.key ? 'on' : ''}`}
                onClick={() => handleSort(column.key)}
                title={column.title || '点击切换排序：升序 → 降序 → 默认'}
                scope="col"
              >
                {column.label}<span className="term-sort" aria-hidden="true">{sortIndicator(sort, column.key)}</span>
              </th>)}
          </tr>
        </thead>
        <tbody>
          {!loading && filtered.length === 0
            ? <tr><td className="term-empty" colSpan={COLUMNS.length}>没有符合条件的数据单元</td></tr>
            : filtered.map(row => <LedgerRow
              key={rowKeyOf(row)}
              row={row}
              onOpenDetail={item => setDetail({ row: item, tab: 'overview' })}
              onOpenDictionary={item => setDetail({ row: item, tab: 'dictionary' })}
            />)}
        </tbody>
      </table>
    </Spin>
    <div className="term-foot">
      表头可排序（↕ ↑ ↓）；点击一行查看概览、表结构与数据字典；审计列（整改需求 / 验收结论 / AI / 体检三维）待 LC-2505 审计数据接入后展示。
      {degraded ? ' 降级视图缺少占用空间、MD 摘要、更新截止与数据止于，均为接口未提供字段。' : ''}
    </div>
    {detail && <TerminalDetail row={detail.row} initialTab={detail.tab} onClose={closeDetail} onDeleted={handleDetailDeleted} />}
  </div>
}
